import { reactive } from 'vue'
import { summarize } from './redact'

// Buffer de logs en memoria para depurar en dispositivo.
// Motivo: el build de produccion tiene drop_console activo (quasar.config.js),
// asi que los console.* se eliminan del bundle. Este logger escribe a un array
// reactivo, no a console, por lo que sobrevive al build y se puede ver en la app.

const MAX_ENTRIES = 500

export const logStore = reactive({
  entries: [],
  enabled: false // oculto por default; se activa con el gesto secreto del LogTracker
})

let counter = 0

function format (value) {
  if (value instanceof Error) {
    return value.message + (value.stack ? '\n' + value.stack : '')
  }
  if (typeof value === 'object' && value !== null) {
    try {
      return JSON.stringify(value)
    } catch (err) {
      return String(value) + ` (no serializable: ${err.message})`
    }
  }
  return String(value)
}

function pad (n, width = 2) {
  return String(n).padStart(width, '0')
}

export function track (level, ...args) {
  const d = new Date()
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`
  logStore.entries.push({
    id: ++counter,
    time,
    level,
    text: args.map(format).join(' ')
  })
  if (logStore.entries.length > MAX_ENTRIES) {
    logStore.entries.shift()
  }
  // Espejo al console nativo (util en dev; se elimina en build por drop_console).
  if (typeof console !== 'undefined' && typeof console[level] === 'function') {
    console[level](...args)
  }
}

export const log = {
  info: (...args) => track('log', ...args),
  warn: (...args) => track('warn', ...args),
  error: (...args) => track('error', ...args)
}

export function clearLogs () {
  logStore.entries.splice(0, logStore.entries.length)
}

let errorCaptureInstalled = false

// Captura de excepciones no manejadas hacia el buffer. Motivo: en release
// `drop_console` borra TODOS los console.*, incluidos los console.error de los
// bloques catch, asi que un error de JS en el dispositivo no deja rastro visible.
// Sentry tambien los recibe, pero esto sirve cuando el equipo esta sin senal.
// Idempotente. Solo escucha: no cancela ni consume los eventos.
//
// Este hook es la unica entrada del buffer que recibe valores ARBITRARIOS: el resto
// llega por `log.*` con argumentos controlados. Por eso cuanto no sea un Error
// pasa antes por `summarize` (ver safeReason abajo).
export function installGlobalErrorCapture () {
  if (errorCaptureInstalled || typeof window === 'undefined') return
  errorCaptureInstalled = true

  window.addEventListener('error', event => {
    if (event.error) track('error', '[JS]', safeReason(event.error))
    else track('error', '[JS]', `${event.message} (${event.filename}:${event.lineno})`)
  })

  window.addEventListener('unhandledrejection', event => {
    track('error', '[PROMISE]', safeReason(event.reason))
  })
}

/**
 * Acota y redacta el motivo de un rechazo antes de que llegue al buffer.
 *
 * Un Error se deja pasar tal cual: `format` ya lo reduce a message + stack, que es
 * corto y es justo lo que se quiere leer. Cualquier OTRA cosa se resume, porque
 * `format` le haria un JSON.stringify sin limite.
 *
 * No es hipotetico: src/api/responser.js rechaza con el objeto `response` de axios,
 * no con un Error, y ese objeto trae `config.data` (el body de la peticion) y
 * `config.headers.Authorization`. En el POST de finalizar ocurrencia el body son las
 * fotos en base64: sin esto, un solo rechazo no atendido escribia decenas de MB al
 * buffer -y el bearer token junto con ellos- en cualquier dispositivo, sin que nadie
 * hubiera hecho el gesto de los 7 taps.
 */
function safeReason (value) {
  return value instanceof Error ? value : summarize(value)
}
